import React, { useRef } from "react";
import { Box, Button, Card, CardContent, IconButton, Stack, Typography } from "@mui/material";
import { Close, Upload, InsertDriveFile } from "@mui/icons-material";
import type { Job } from "../../../../../api/types";
import { getValidationErrorKey } from "../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../admin/ImportSettings";
import { ColumnContext } from "../standard-column-types";
import { useFieldNotesColumnViewModel } from "./FieldNotes.column.vm";
import { useValidationErrors } from "../../ValidationErrorsContext";

export interface FieldNotesColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly FieldNotes column component.
 * Provides file upload and management for field notes attachments.
 */
export const FieldNotesColumnMobile: React.FC<FieldNotesColumnMobileProps> = ({
	job,
	columnContext,
	disabled = false,
	label,
}) => {
	const vm = useFieldNotesColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const validationKey = "fieldNotes";
	const errorKey = getValidationErrorKey(validationKey, job.id);
	const hasError = !!validationErrors[errorKey];
	const files = vm.getCurrentFiles(job);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;
		if (!selectedFiles || selectedFiles.length === 0) return;

		vm.handleAddFiles(job.id, selectedFiles);

		// Clear the input so the same file can be selected again
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleRemoveFile = (index: number) => {
		if (disabled) return;
		vm.handleRemoveFile(job.id, index);
	};

	return (
		<Card variant="outlined" sx={{ mb: 2 }}>
			<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
				<Typography variant="subtitle2" color={hasError ? "error" : "text.secondary"} gutterBottom>
					{label || columnDisplayNames.fieldNotes}
				</Typography>

				{/* File list */}
				{files.length > 0 && (
					<Stack spacing={1} sx={{ mb: 2 }}>
						{files.map((file, index) => (
							<Box
								key={index}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									p: 1,
									bgcolor: "action.hover",
									borderRadius: 1,
								}}
							>
								<InsertDriveFile fontSize="small" color="action" />
								<Typography
									variant="body2"
									sx={{
										flex: 1,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{file.name}
								</Typography>
								<IconButton size="small" onClick={() => handleRemoveFile(index)} disabled={disabled} color="error">
									<Close fontSize="small" />
								</IconButton>
							</Box>
						))}
					</Stack>
				)}

				{/* Upload button */}
				<input
					ref={fileInputRef}
					type="file"
					multiple
					style={{ display: "none" }}
					onChange={handleFileSelect}
					disabled={disabled}
				/>
				<Button
					variant="outlined"
					fullWidth
					startIcon={<Upload />}
					onClick={() => fileInputRef.current?.click()}
					disabled={disabled}
					sx={{ minHeight: 44 }}
				>
					Upload Files
				</Button>

				{/* Error message */}
				{hasError && (
					<Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
						{validationErrors[errorKey]}
					</Typography>
				)}
			</CardContent>
		</Card>
	);
};
